import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Task timer hook — only triggers parent re-render when timer expires.
 * Uses localStorage for persistence and cross-component communication.
 * 
 * Usage:
 *   const { isExpired, stopTimer } = useTaskTimer(600, shouldRun, sessionId)
 */
export function useTaskTimer(durationSeconds, shouldRun, sessionId) {
  const [isExpired, setIsExpired] = useState(false)
  const stoppedRef = useRef(false)
  const intervalRef = useRef(null)

  // Save start time on first run
  useEffect(() => {
    if (!shouldRun || !sessionId || stoppedRef.current) return

    const key = `timer_start_${sessionId}`
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, Date.now().toString())
    }
  }, [shouldRun, sessionId])

  // Check expiry every second (no state updates until expired)
  useEffect(() => {
    if (!shouldRun || !sessionId || stoppedRef.current) return

    const key = `timer_start_${sessionId}`

    const checkExpiry = () => {
      const savedStart = localStorage.getItem(key)
      if (!savedStart) return

      const elapsed = Math.floor((Date.now() - parseInt(savedStart, 10)) / 1000)
      if (elapsed >= durationSeconds) {
        clearInterval(intervalRef.current)
        setIsExpired(true)
      }
    }

    checkExpiry()
    intervalRef.current = setInterval(checkExpiry, 1000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [shouldRun, sessionId, durationSeconds])

  const stopTimer = useCallback(() => {
    stoppedRef.current = true
    if (intervalRef.current) clearInterval(intervalRef.current)
    // Signal display component to stop
    if (sessionId) {
      localStorage.setItem(`timer_done_${sessionId}`, 'true')
    }
  }, [sessionId])

  return { isExpired, stopTimer }
}